import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Button,
  Chip,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import newsIcon from "../assets/icons/ic_info_re.png";
import "./Notice.scss";
import "./Common.scss";
import {
  useNotice,
  formatDisplayDate,
  isNoticeNew,
} from "../hooks/logic/useNotice";
import { ResponseGetNoticeResponse } from "../services/OpenAPI";
import { isOwner } from "../constants/roles";

const Notice = () => {
  const {
    loginUser,
    toastOpen,
    toastMessage,
    toastSeverity,
    handleCloseToast,
    notices,
    addNotice,
    updateNotice,
    deleteNotice,
  } = useNotice();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNotice, setEditingNotice] =
    useState<ResponseGetNoticeResponse | null>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  // Check if current user is admin
  const isAdmin = isOwner(loginUser?.permissionId);

  const openAddDialog = () => {
    setEditingNotice(null);
    setTitle("");
    setContent("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (editingNotice) {
      const updated: ResponseGetNoticeResponse = {
        ...editingNotice,
        title,
        content,
      };
      await updateNotice(updated);
    } else {
      await addNotice(title, content);
    }
    setDialogOpen(false);
  };

  const handleDelete = async (id: number) => {
    await deleteNotice(id);
    if (editingNotice && editingNotice.id === id) {
      setEditingNotice(null);
      setTitle("");
      setContent("");
    }
  };

  return (
    <Box className="notice-section">
      <Typography className="title upper-title">
        <img src={newsIcon} alt="お知らせアイコン" />
        お知らせ
      </Typography>

      <Paper variant="outlined" sx={{ height: 260, p: 2, overflowY: "auto" }}>
        {notices.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            {/* 表示するお知らせはありません。 */}
          </Typography>
        ) : (
          notices.map((msg) => {
            const displayDate = formatDisplayDate(msg.displayAt);
            const isNew = isNoticeNew(msg.displayAt);

            return (
              <Box
                key={msg.id ?? msg.content}
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{
                  mb: 1.5,
                  p: 1,
                  backgroundColor: "#fafafa",
                  borderRadius: "4px",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {displayDate && (
                      <Typography variant="caption" color="textSecondary">
                        {displayDate}
                      </Typography>
                    )}
                    {isNew && (
                      <Chip
                        label="NEW"
                        size="small"
                        sx={{
                          height: 20,
                          backgroundColor: "#ff5722",
                          color: "white",
                          fontSize: "0.65rem",
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      wordBreak: "break-word",
                    }}
                  >
                    {msg.title || msg.content}
                  </Typography>
                </Box>

                {isAdmin && (
                  <Box flexShrink={0}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingNotice(msg);
                        setTitle(msg.title || "");
                        setContent(msg.content || "");
                        setDialogOpen(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {msg.id !== undefined && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(msg.id!)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </Paper>

      {isAdmin && (
        <IconButton
          className="add-button"
          color="primary"
          onClick={openAddDialog}
        >
          <AddIcon />
        </IconButton>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingNotice ? "お知らせを編集" : "お知らせを追加"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            // label="タイトル"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          {/* <TextField
            margin="dense"
            label="内容"
            fullWidth
            multiline
            minRows={3}
            value={content}
            onChange={(e) => setContent(e.target.value)}
          /> */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
          {editingNotice && editingNotice.id !== undefined && (
            <Button
              color="error"
              onClick={() => {
                handleDelete(editingNotice.id!);
                setDialogOpen(false);
              }}
            >
              削除
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={!title.trim() /* || !content.trim() */}
            variant="contained"
          >
            {editingNotice ? "保存" : "追加"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={handleCloseToast}
          severity={toastSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {toastMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default Notice;
